import { logDev } from '../index';

export interface SnapshotData {
    base64: string;
    width: number;
    height: number;
    frames: number;
    animation: string;
    interval: number;
    palette: string[];
}

export interface SpriteSheetData {
    base64: string;
    originalBase64: string;
    width: number;
    height: number;
    frames: number;
    animation: string;
    interval: number;
    isDirty: boolean;
    history: SnapshotData[];
    historyIndex: number;
    palette: string[];
    baseSnapshot?: SnapshotData;
}

export class HistoryManager {
    constructor(private app: any) { }

    takeSnapshot() {
        if (this.app.isLoading) {
            logDev(`[Snapshot] Skipped: Still loading`);
            return;
        }

        // Always use the data canvas (off-screen) to get a canonically-encoded base64.
        // '#sl-sheet-canvas' is the VIEW canvas, populated asynchronously inside updatePreview → img.onload,
        // so reading it synchronously would capture the OLD state before any transform committed.
        // this.app.dataCanvas is the source-of-truth and is always updated synchronously before takeSnapshot().
        const dataCanvas = this.app.dataCanvas as HTMLCanvasElement;
        const currentData = (dataCanvas && dataCanvas.width > 0 && dataCanvas.height > 0)
            ? dataCanvas.toDataURL()
            : (this.app.currentBase64 || '');
        const key = `${this.app.projectId}:${this.app.itemId}:${this.app.currentSheet}`;
        const sheetData = this.app.sessionSheets.get(key);
        if (!sheetData) {
            logDev(`[Snapshot] Skipped: No sheet data for ${key}`);
            return;
        }

        const snapshot: SnapshotData = {
            base64: currentData,
            width: parseInt((this.app.container.querySelector('#sl-meta-width') as HTMLInputElement).value) || 32,
            height: parseInt((this.app.container.querySelector('#sl-meta-height') as HTMLInputElement).value) || 32,
            frames: parseInt((this.app.container.querySelector('#sl-meta-frames') as HTMLInputElement).value) || 1,
            animation: sheetData.animation || 'sequence',
            interval: sheetData.interval || 0.2,
            palette: [...(this.app.colorPicker?.getPalette() || [])]
        };

        if (!sheetData.history) {
            sheetData.history = [snapshot];
            sheetData.historyIndex = 0;
            logDev(`[Snapshot] Initialized history for ${key}`);
        }

        const lastSnapshot = sheetData.history[sheetData.historyIndex];

        if (lastSnapshot.base64 === snapshot.base64 &&
            lastSnapshot.width === snapshot.width &&
            lastSnapshot.height === snapshot.height &&
            lastSnapshot.frames === snapshot.frames &&
            lastSnapshot.animation === snapshot.animation &&
            lastSnapshot.interval === snapshot.interval) {
            logDev(`[Snapshot] Skipped: No changes detected for ${key}`);
            return;
        }

        if (sheetData.historyIndex < sheetData.history.length - 1) {
            const truncatedCount = sheetData.history.length - 1 - sheetData.historyIndex;
            sheetData.history = sheetData.history.slice(0, sheetData.historyIndex + 1);
            logDev(`[Snapshot] Truncating history: removed ${truncatedCount} future states`);
        }

        sheetData.history.push(snapshot);
        sheetData.historyIndex++;
        logDev(`[Snapshot] Recorded: ${key}, history: ${sheetData.historyIndex + 1}, index: ${sheetData.historyIndex}`);

        if (sheetData.history.length > 50) {
            sheetData.history.shift();
            sheetData.historyIndex--;
            logDev(`[Snapshot] History limit reached (50), shifted oldest state`);
        }

        sheetData.base64 = snapshot.base64;
        sheetData.width = snapshot.width;
        sheetData.height = snapshot.height;
        sheetData.frames = snapshot.frames;
        sheetData.animation = snapshot.animation;
        sheetData.interval = snapshot.interval;
        sheetData.palette = [...snapshot.palette];

        this.app.currentBase64 = snapshot.base64;

        this.app.checkDirty();
        this.app.updateButtonStates();
        this.app.renderHistoryList();
    }

    async undo() {
        if (this.app.isLoading) return;

        const key = `${this.app.projectId}:${this.app.itemId}:${this.app.currentSheet}`;
        const sheetData = this.app.sessionSheets.get(key);
        if (!sheetData || !sheetData.history || sheetData.historyIndex <= 0) {
            logDev(`[Snapshot] Undo skipped: No more history for ${key}`);
            return;
        }

        sheetData.historyIndex--;
        const snapshot = sheetData.history[sheetData.historyIndex];
        logDev(`[Snapshot] Undo: ${key}, new index: ${sheetData.historyIndex}`);

        await this.restoreSnapshot(snapshot, sheetData);
        this.app.renderHistoryList();
    }

    async redo() {
        if (this.app.isLoading) return;

        const key = `${this.app.projectId}:${this.app.itemId}:${this.app.currentSheet}`;
        const sheetData = this.app.sessionSheets.get(key);
        if (!sheetData || !sheetData.history || sheetData.historyIndex >= sheetData.history.length - 1) {
            logDev(`[Snapshot] Redo skipped: At latest state for ${key}`);
            return;
        }

        sheetData.historyIndex++;
        const snapshot = sheetData.history[sheetData.historyIndex];
        logDev(`[Snapshot] Redo: ${key}, new index: ${sheetData.historyIndex}`);

        await this.restoreSnapshot(snapshot, sheetData);
        this.app.renderHistoryList();
    }

    async restoreSnapshot(snapshot: SnapshotData, sheetData: SpriteSheetData) {
        if (this.app.isLoading) {
            logDev(`[Snapshot] Restore skipped: Still loading`);
            return;
        }
        logDev(`[Snapshot] Restoring sheet with metadata...`);

        sheetData.base64 = snapshot.base64;
        sheetData.width = snapshot.width;
        sheetData.height = snapshot.height;
        sheetData.frames = snapshot.frames;
        sheetData.animation = snapshot.animation;
        sheetData.interval = snapshot.interval;
        sheetData.palette = [...snapshot.palette];

        (this.app.container.querySelector('#sl-meta-width') as HTMLInputElement).value = snapshot.width.toString();
        (this.app.container.querySelector('#sl-meta-height') as HTMLInputElement).value = snapshot.height.toString();
        (this.app.container.querySelector('#sl-meta-frames') as HTMLInputElement).value = snapshot.frames.toString();

        const animSelect = this.app.container.querySelector('#sl-meta-animation') as HTMLSelectElement;
        if(animSelect) animSelect.value = snapshot.animation;

        this.app.updateInterval(snapshot.interval);
        // Palette is persistent and NOT restored from history snapshots.

        (this.app.container.querySelector('#sl-scale-width') as HTMLInputElement).value = snapshot.width.toString();
        (this.app.container.querySelector('#sl-scale-height') as HTMLInputElement).value = snapshot.height.toString();

        await this.app.updatePreview(snapshot.base64, true);

        this.app.checkDirty();
        this.app.updateButtonStates();
        logDev(`[Snapshot] Restore triggered`);
    }

    async loadHistorySnapshot(index: number) {
        if (this.app.isLoading) return;

        const key = `${this.app.projectId}:${this.app.itemId}:${this.app.currentSheet}`;
        const sheetData = this.app.sessionSheets.get(key);
        if (!sheetData || !sheetData.history) return;

        if (index < 0 || index >= sheetData.history.length) return;

        logDev(`[Snapshot] Loading from list: ${key}, target index: ${index}`);

        sheetData.historyIndex = index;
        const snapshot = sheetData.history[index];

        await this.restoreSnapshot(snapshot, sheetData);
        this.app.renderHistoryList();
    }
}
